import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  History, 
  FileText, 
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  User,
  Filter,
  X,
  Upload,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Cloud
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { useAuth } from '../App';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// French month names
const MONTHS = [
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' }
];

// Generate years (current year to 5 years ago)
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => ({
  value: String(currentYear - i),
  label: String(currentYear - i)
}));

// Generate days 1-31
const DAYS = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1).padStart(2, '0'),
  label: String(i + 1)
}));

export default function HistoriqueAchats() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalSuccess, setTotalSuccess] = useState(0);
  const [totalDoublon, setTotalDoublon] = useState(0);
  const [totalFailed, setTotalFailed] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [page, setPage] = useState(0);
  const [filterOptions, setFilterOptions] = useState({ users: [], sources: [] });
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters state
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('all');
  
  const limit = 50;

  // Build date filter string for API
  const buildDateFilter = useCallback(() => {
    if (yearFilter === 'all') return null;
    let dateStr = yearFilter;
    if (monthFilter !== 'all') {
      dateStr += `-${monthFilter}`;
      if (dayFilter !== 'all') {
        dateStr += `-${dayFilter}`;
      }
    }
    return dateStr;
  }, [yearFilter, monthFilter, dayFilter]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (sourceFilter !== 'all') params.source_filter = sourceFilter;
      if (userFilter !== 'all') params.user_filter = userFilter;
      
      const dateFilter = buildDateFilter();
      if (dateFilter) params.date_filter = dateFilter;
      
      const res = await axios.get(`${API}/api/supplier-invoices/history/imports`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setHistory(res.data.history || []);
      setTotal(res.data.total || 0);
      setTotalSuccess(res.data.total_success || 0);
      setTotalDoublon(res.data.total_doublon || 0);
      setTotalFailed(res.data.total_failed || 0);
      setTotalAll(res.data.total_all || 0);
      if (res.data.filters) {
        setFilterOptions(res.data.filters);
      }
    } catch (err) {
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter, sourceFilter, userFilter, buildDateFilter]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [statusFilter, sourceFilter, userFilter, yearFilter, monthFilter, dayFilter]);

  // Reset month and day when year changes to 'all'
  useEffect(() => {
    if (yearFilter === 'all') {
      setMonthFilter('all');
      setDayFilter('all');
    }
  }, [yearFilter]);

  // Reset day when month changes to 'all'
  useEffect(() => {
    if (monthFilter === 'all') {
      setDayFilter('all');
    }
  }, [monthFilter]);

  const clearFilters = () => {
    setStatusFilter('all');
    setSourceFilter('all');
    setUserFilter('all');
    setYearFilter('all');
    setMonthFilter('all');
    setDayFilter('all');
  };

  const hasActiveFilters = statusFilter !== 'all' || sourceFilter !== 'all' || userFilter !== 'all' || yearFilter !== 'all';

  // Source labels mapping - uniquement les sources existantes
  const sourceLabels = {
    'all': 'Toutes les sources',
    'import': 'Import PDF',
    'onedrive': 'OneDrive'
  };

  const getSourceBadge = (source) => {
    if (source === 'onedrive') {
      return (
        <Badge className="bg-blue-100 text-blue-700 border-blue-200">
          <Cloud className="w-3 h-3 mr-1" />
          OneDrive
        </Badge>
      );
    }
    return (
      <Badge className="bg-purple-100 text-purple-700 border-purple-200">
        <Upload className="w-3 h-3 mr-1" />
        Import PDF
      </Badge>
    );
  };

  const getStatusBadge = (item) => {
    if (item.type === 'success') {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Réussi
        </Badge>
      );
    }
    if (item.type === 'doublon') {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 cursor-help">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Doublon
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">{item.error || 'Facture déjà existante'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className="bg-red-100 text-red-700 border-red-200 cursor-help">
              <XCircle className="w-3 h-3 mr-1" />
              {item.error_label || 'Échec'}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="text-sm">{item.error || 'Erreur inconnue'}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Navigate to invoice detail
  const handleRowClick = (item) => {
    if (item.type === 'success' && item.invoice_id) {
      navigate(`/factures-fournisseurs?invoice=${encodeURIComponent(item.invoice_id)}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Paris'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    });
  };

  const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const totalPages = Math.ceil(total / limit);

  // Status tabs
  const statusTabs = [
    { value: 'all', label: 'Tous', count: totalAll, bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
    { value: 'success', label: 'Réussis', count: totalSuccess, bgColor: 'bg-green-100', textColor: 'text-green-700' },
    { value: 'doublon', label: 'Doublons', count: totalDoublon, bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' },
    { value: 'failed', label: 'Échecs', count: totalFailed, bgColor: 'bg-red-100', textColor: 'text-red-700' }
  ];

  return (
    <div className="p-6 space-y-6" data-testid="historique-achats-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <History className="w-8 h-8 text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold">Historique des imports - Achats</h1>
            <p className="text-muted-foreground">
              Traçabilité des factures fournisseurs importées (réussites et échecs)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={showFilters ? "default" : "outline"} 
            onClick={() => setShowFilters(!showFilters)}
            className="relative"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtres
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
            )}
          </Button>
          <Button variant="outline" onClick={fetchHistory} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex gap-2 border-b">
        {statusTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              statusFilter === tab.value
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${tab.bgColor} ${tab.textColor}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-orange-200 bg-orange-50/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-orange-900 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filtres
              </h3>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-orange-600 hover:text-orange-700">
                  <X className="w-4 h-4 mr-1" />
                  Réinitialiser
                </Button>
              )}
            </div>
            <div className="grid grid-cols-5 gap-4">
              {/* Source Filter */}
              <div className="space-y-2">
                <Label className="text-sm text-orange-800">Source</Label>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes les sources" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(sourceLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* User Filter */}
              <div className="space-y-2">
                <Label className="text-sm text-orange-800">Importé par</Label>
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {filterOptions.users?.map((user) => (
                      <SelectItem key={user} value={user}>{user}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Year Filter */}
              <div className="space-y-2">
                <Label className="text-sm text-orange-800">Année</Label>
                <Select value={yearFilter} onValueChange={setYearFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les années</SelectItem>
                    {YEARS.map((year) => (
                      <SelectItem key={year.value} value={year.value}>{year.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Month Filter */}
              <div className="space-y-2">
                <Label className="text-sm text-orange-800">Mois</Label>
                <Select 
                  value={monthFilter} 
                  onValueChange={setMonthFilter}
                  disabled={yearFilter === 'all'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les mois</SelectItem>
                    {MONTHS.map((month) => (
                      <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Day Filter */}
              <div className="space-y-2">
                <Label className="text-sm text-orange-800">Jour</Label>
                <Select 
                  value={dayFilter} 
                  onValueChange={setDayFilter}
                  disabled={monthFilter === 'all'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les jours</SelectItem>
                    {DAYS.map((day) => (
                      <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalAll}</p>
              <p className="text-sm text-muted-foreground">Total imports</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{totalSuccess}</p>
              <p className="text-sm text-muted-foreground">Réussis</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{totalDoublon}</p>
              <p className="text-sm text-muted-foreground">Doublons</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{totalFailed}</p>
              <p className="text-sm text-muted-foreground">Échecs</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* History Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            Liste des imports
            {hasActiveFilters && (
              <Badge variant="outline" className="text-orange-600 border-orange-300">
                Filtres actifs
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{hasActiveFilters ? 'Aucun résultat pour ces filtres' : "Aucun historique d'import"}</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Réinitialiser les filtres
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="max-h-[500px] overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date import</TableHead>
                      <TableHead>Fichier</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Numéro</TableHead>
                      <TableHead>Fournisseur</TableHead>
                      <TableHead>Montant TTC</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Importé par</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((item, index) => (
                      <TableRow 
                        key={`${item.id}-${index}`}
                        className={`${item.type === 'failed' ? 'bg-red-50/50' : item.type === 'doublon' ? 'bg-yellow-50/50' : ''} ${item.type === 'success' && item.invoice_id ? 'cursor-pointer hover:bg-orange-50' : ''}`}
                        onClick={() => handleRowClick(item)}
                      >
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDateTime(item.created_at)}
                        </TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate" title={item.filename}>
                          {item.filename || '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(item)}</TableCell>
                        <TableCell className="font-medium">
                          {item.numero || '-'}
                        </TableCell>
                        <TableCell>{item.fournisseur || '-'}</TableCell>
                        <TableCell className="font-medium">
                          {item.total_ttc ? formatMoney(item.total_ttc) : '-'}
                        </TableCell>
                        <TableCell>{getSourceBadge(item.source)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <User className="w-3 h-3" />
                            {item.uploaded_by}
                            {item.type === 'success' && item.invoice_id && (
                              <ExternalLink className="w-3 h-3 text-orange-500" />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page + 1} sur {totalPages} ({total} éléments)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
